import {
  GroupNamesOptionNames,
  GroupNamesOptionNamesIntl,
  SpoIntlType,
  SpoType,
  StockInputValue_Edit_NoOption,
  StockInputValue_Edit_Option,
  StockInputValue_Edit_Option_Intl,
  StockInputValue_New_Option,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/types.ts';
import { Lang } from '../../../../../../../../shared/type';
import {
  PureInput,
  PureInputType,
  pureInputVariant,
} from '../../../../../../../../shared/rune-component/Input/PureInput.ts';
import {
  NumberTextInput,
  NumberTextInputType,
} from '../../../../../../../../shared/rune-component/Input/NumberTextInput.ts';
import { SPO_ITEM_STOCK__MAX } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/constants.ts';

export const stockDataUtil = {
  formatOptionNames(groupNamesOptionNames: GroupNamesOptionNames[]): string {
    return groupNamesOptionNames.map((item) => item.option_name).join(' / ');
  },

  formatOptionNamesIntl(
    groupNamesOptionNames: GroupNamesOptionNamesIntl[],
    lang: Extract<Lang, typeof Lang.en | typeof Lang.jp>,
  ): string {
    return groupNamesOptionNames
      .map((item) => {
        return lang === Lang.en ? item.option_name_en : item.option_name_jp;
      })
      .join(' / ');
  },

  isSpoIntlType(spo: SpoType | SpoIntlType): spo is SpoIntlType {
    const firstOption = spo._.spo_options[0];
    return !!firstOption && 'name_en' in firstOption;
  },

  formatName(sku?: string): string {
    return sku || '';
  },

  toNewOptionData(spo: SpoType | SpoIntlType): StockInputValue_New_Option[] {
    return spo._.spo_items.map((item) => ({
      name: this.formatOptionNames(item.group_names_option_names),
      price: item.price,
      stock: item._.spo_item_stock.quantity,
      sku: item.sku?.nickname,
    }));
  },

  toEditNoOptionData(spo: SpoType | SpoIntlType): StockInputValue_Edit_NoOption[] {
    const firstItem = spo._.spo_items[0];
    return [
      {
        stock: firstItem?._.spo_item_stock.quantity || 0,
        sold_quantity: firstItem?.sold_quantity || 0,
        sku: firstItem?.sku?.nickname,
      },
    ];
  },

  toEditOptionData(spo: SpoType): StockInputValue_Edit_Option[] {
    return spo._.spo_items.map((item) => ({
      name: this.formatOptionNames(item.group_names_option_names),
      price: item.price,
      stock: item._.spo_item_stock.quantity,
      sold_quantity: item.sold_quantity,
      sku: item.sku?.nickname,
    }));
  },

  toEditOptionIntlData(spo: SpoIntlType): StockInputValue_Edit_Option_Intl[] {
    return spo._.spo_items.map((item) => ({
      name: this.formatOptionNames(item.group_names_option_names),
      name_en: this.formatOptionNamesIntl(item.group_names_option_names, Lang.en),
      name_jp: this.formatOptionNamesIntl(item.group_names_option_names, Lang.jp),
      price: item.price,
      stock: item._.spo_item_stock.quantity,
      sold_quantity: item.sold_quantity,
      sku: item.sku?.nickname,
    }));
  },

  textInput({
    index,
    name,
    value,
    maxlength = 50,
    onFocusout,
    placeholder,
  }: {
    index: number;
    name: string;
    value?: string;
    placeholder?: string;
    maxlength?: number;
    onFocusout?: (view: PureInputType) => void;
  }) {
    return new PureInput({
      variant: pureInputVariant.studio_editor_stock_option,
      index,
      type: 'text',
      name,
      value,
      maxlength,
      onFocusout,
      placeholder,
    });
  },

  numberInput({
    index,
    name,
    value = 0,
    min = 0,
    max = SPO_ITEM_STOCK__MAX,
    placeholder,
    onFocusout,
  }: {
    index: number;
    name: string;
    value?: number;
    placeholder?: string;
    maxlength?: number;
    min?: number;
    max?: number;
    onFocusout?: (view: NumberTextInputType) => void;
  }) {
    return new NumberTextInput({
      variant: pureInputVariant.studio_editor_stock_option,
      index,
      name,
      value,
      placeholder,
      min,
      max,
      onFocusout,
    });
  },
};
